import { useEffect, useRef, useState } from "react";
import { Sparkles, Loader2, Send, FileSignature, ArrowLeft, Bot, User, Download, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SimpleMarkdown } from "@/components/SimpleMarkdown";
import { nb } from "@/lib/format";
import { SignatoryFields, validateSignatories } from "./SignatoryFields";

const LABEL = {
  kompleks_salg: { title: "Kompleks salg (SPA)", desc: "AI-veiviseren stiller spørsmål og lager en komplett aksjekjøpsavtale." },
  emisjon: { title: "Emisjon (kapitalforhøyelse)", desc: "AI-veiviseren stiller spørsmål og lager en emisjonsavtale. Krever styre-/GF-vedtak før gjennomføring." },
};

export function AgreementWizard({ companyId, rows, classes, kind, open, onOpenChange, onCreated }) {
  const isEm = kind === "emisjon";
  const [phase, setPhase] = useState("interview"); // interview | review
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState(null); // {summary, agreement_markdown, terms, suggested}
  const [streaming, setStreaming] = useState(false);
  const [streamedMd, setStreamedMd] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const scrollRef = useRef();

  // executable form
  const [f, setF] = useState({ title: "", summary: "", share_class: "", shares: "", price_per_share: "", seller_shareholder_id: "", cpKind: "new_company", cp_shareholder_id: "", cp_name: "", cp_id_number: "", cp_email: "", seller_signatories: [], cp_signatories: [], cp_name_verified: false });

  const sellerRow = rows.find((r) => r.id === f.seller_shareholder_id);
  const sellerClasses = sellerRow ? Object.keys(sellerRow.holdings).filter((k) => sellerRow.holdings[k] > 0) : [];
  const sellerIsCompany = !isEm && sellerRow?.holder_type === "selskap";
  const cpRow = rows.find((r) => r.id === f.cp_shareholder_id);
  const cpIsCompany = f.cpKind === "new_company" || (f.cpKind === "existing" && cpRow?.holder_type === "selskap");

  const currentMd = streamedMd || draft?.agreement_markdown || "";
  const wordCount = currentMd ? currentMd.trim().split(/\s+/).filter(Boolean).length : 0;
  const progressPct = streaming ? Math.min(96, Math.max(6, Math.round((currentMd.length / 7000) * 100))) : 100;

  const downloadDraft = async () => {
    setDownloading(true);
    try {
      const cpName = f.cpKind === "existing" ? (rows.find((r) => r.id === f.cp_shareholder_id)?.name || "") : f.cp_name;
      const res = await fetch(`${API}/companies/${companyId}/agreements/preview-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          kind, title: f.title, summary: f.summary, agreement_markdown: currentMd,
          share_class: f.share_class, shares: parseInt(f.shares, 10) || 0, price_per_share: parseFloat(f.price_per_share || 0),
          counterparty_name: cpName || "", seller_name: isEm ? "" : (sellerRow?.name || ""),
        }),
      });
      if (!res.ok) throw new Error("pdf failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(f.title || "avtale").replace(/\s+/g, "_")}_utkast.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Kunne ikke laste ned avtalen. Prøv igjen.");
    } finally {
      setDownloading(false);
    }
  };

  const generateAgreement = async (msgs, terms) => {
    setStreaming(true);
    setStreamedMd("");
    try {
      const res = await fetch(`${API}/companies/${companyId}/agreements/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ kind, messages: msgs, terms: terms || {} }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setStreamedMd(acc);
      }
      acc += decoder.decode();
      setStreamedMd(acc);
      setDraft((d) => ({ ...(d || {}), agreement_markdown: acc }));
    } catch (e) {
      toast.error("Kunne ikke generere avtaleteksten. Prøv igjen.");
    } finally {
      setStreaming(false);
    }
  };

  const ask = async (nextMessages) => {
    setThinking(true);
    setFailed(false);
    try {
      const { data } = await api.post(`/companies/${companyId}/agreements/interview`, { kind, messages: nextMessages });
      if (data.done) {
        const sug = data.suggested || {};
        setDraft({ ...data, agreement_markdown: "" });
        setF((p) => ({
          ...p,
          title: sug.title || (isEm ? "Emisjonsavtale" : "Aksjekjøpsavtale"),
          summary: data.summary || "",
          share_class: sug.share_class || p.share_class || (classes?.[0]?.name ?? "Ordinære aksjer"),
          shares: sug.shares ? String(sug.shares) : p.shares,
          price_per_share: sug.price_per_share ? String(sug.price_per_share) : p.price_per_share,
          cp_name: sug.counterparty_name || p.cp_name,
        }));
        setPhase("review");
        generateAgreement(nextMessages, data.terms || {});
      } else {
        setMessages([...nextMessages, { role: "assistant", content: data.message }]);
      }
    } catch (e) {
      setFailed(true);
      toast.error(formatApiError(e.response?.data?.detail) || "AI-en svarte ikke. Prøv igjen.");
    } finally {
      setThinking(false);
    }
  };

  useEffect(() => {
    if (open) {
      setPhase("interview");
      setMessages([]);
      setInput("");
      setDraft(null);
      setStreamedMd("");
      setStreaming(false);
      ask([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, thinking]);

  const send = () => {
    const text = input.trim();
    if (!text) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    ask(next);
  };

  const create = async () => {
    const shares = parseInt(f.shares, 10);
    if (!f.share_class || !shares) return toast.error("Fyll inn aksjeklasse og antall aksjer");
    if (!isEm && !f.seller_shareholder_id) return toast.error("Velg selger");
    const counterparty =
      f.cpKind === "existing"
        ? { kind: "existing", shareholder_id: f.cp_shareholder_id }
        : f.cpKind === "new_person"
        ? { kind: "new_person", name: f.cp_name, id_number: f.cp_id_number, email: f.cp_email }
        : { kind: "new_company", name: f.cp_name, id_number: f.cp_id_number };
    if (f.cpKind === "existing" && !f.cp_shareholder_id) return toast.error(isEm ? "Velg tegner" : "Velg kjøper");
    if (f.cpKind !== "existing" && !f.cp_name) return toast.error("Skriv inn navn på motpart");
    if (f.cpKind === "new_person" && !f.cp_email?.trim()) return toast.error("Skriv inn e-post på motpart (fødselsnummer kommer via BankID)");
    if (cpIsCompany) {
      const err = validateSignatories(f.cp_signatories, f.cpKind === "existing" ? cpRow?.name : f.cp_name);
      if (err) return toast.error(err);
    }
    if (sellerIsCompany) {
      const err = validateSignatories(f.seller_signatories, sellerRow?.name);
      if (err) return toast.error(err);
    }
    if (cpIsCompany) {
      counterparty.signatories = f.cp_signatories;
    }
    const seller_signatories = sellerIsCompany ? f.seller_signatories : undefined;
    setSaving(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/agreements`, {
        kind, title: f.title, summary: f.summary, agreement_markdown: draft?.agreement_markdown || streamedMd || "", terms: draft?.terms || {},
        share_class: f.share_class, shares, price_per_share: parseFloat(f.price_per_share || 0),
        seller_shareholder_id: isEm ? null : f.seller_shareholder_id, seller_signatories, counterparty,
      });
      toast.success(isEm ? "Emisjon opprettet – klar for vedtak" : "Avtale opprettet – klar for signering");
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const cpLabel = isEm ? "Tegner (mottar nye aksjer)" : "Kjøper";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-hidden sm:max-w-2xl" data-testid="agreement-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> {LABEL[kind].title}</DialogTitle>
          <DialogDescription>{LABEL[kind].desc}</DialogDescription>
        </DialogHeader>

        {phase === "interview" ? (
          <div className="flex flex-col" style={{ maxHeight: "70vh" }}>
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto pr-1" data-testid="wizard-chat">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`wizard-msg-${m.role}`}>
                  <div className={`grid h-7 w-7 shrink-0 place-items-center rounded-full ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}`}>
                    {m.role === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                  </div>
                  <div className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>{m.content}</div>
                </div>
              ))}
              {thinking ? (
                <div className="flex gap-2.5" data-testid="wizard-thinking">
                  <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/10 text-primary"><Bot className="h-4 w-4" /></div>
                  <div className="flex items-center gap-2 rounded-2xl bg-muted px-3.5 py-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Tenker …</div>
                </div>
              ) : null}
              {failed && !thinking ? (
                <div className="flex justify-center py-1">
                  <Button variant="outline" size="sm" className="gap-1.5" data-testid="wizard-retry-btn" onClick={() => ask(messages)}>
                    <Loader2 className="h-4 w-4" /> Prøv igjen
                  </Button>
                </div>
              ) : null}
            </div>
            <div className="mt-3 flex items-end gap-2 border-t border-border/60 pt-3">
              <Textarea
                data-testid="wizard-input"
                rows={2}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Skriv svaret ditt …"
                className="resize-none"
                disabled={thinking}
              />
              <Button onClick={send} disabled={thinking || !input.trim()} className="h-10 shrink-0 gap-1.5" data-testid="wizard-send-btn">
                <Send className="h-4 w-4" /> Send
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 overflow-y-auto pr-1" style={{ maxHeight: "72vh" }}>
            <div className="rounded-xl border border-border/60 bg-muted/30 p-4" data-testid="wizard-agreement-preview">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <FileSignature className="h-3.5 w-3.5" /> Generert avtale
              </p>
              {streaming ? (
                <div className="mb-3" data-testid="wizard-progress">
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1 text-primary" data-testid="wizard-streaming"><Loader2 className="h-3 w-3 animate-spin" /> AI skriver avtalen …</span>
                    <span data-testid="wizard-progress-count">{wordCount} ord</span>
                  </div>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full bg-primary transition-all duration-300 ease-out" style={{ width: `${progressPct}%` }} />
                  </div>
                </div>
              ) : null}
              {currentMd ? (
                <SimpleMarkdown text={currentMd} />
              ) : !streaming ? (
                <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> AI skriver avtalen …
                </div>
              ) : null}
            </div>

            <div className="rounded-xl border border-primary/30 bg-primary/[0.03] p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-primary">Bekreft detaljene som gjennomføres i aksjeeierboka</p>
              <div className="space-y-3">
                <div><Label className="text-xs">Tittel</Label><Input className="mt-1.5" data-testid="wizard-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>

                {!isEm ? (
                  <div>
                    <Label className="text-xs">Selger (aksjonær)</Label>
                    <Select value={f.seller_shareholder_id} onValueChange={(v) => setF({ ...f, seller_shareholder_id: v, share_class: "" })}>
                      <SelectTrigger className="mt-1.5" data-testid="wizard-seller"><SelectValue placeholder="Velg selger" /></SelectTrigger>
                      <SelectContent>{rows.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} ({nb(r.shares)} aksjer)</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ) : null}

                {sellerIsCompany ? (
                  <SignatoryFields companyName={sellerRow?.name} testIdPrefix="wizard-seller-signatory"
                    orgNumber={sellerRow?.id_number}
                    value={f.seller_signatories} onChange={(v) => setF({ ...f, seller_signatories: v })} />
                ) : null}

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Aksjeklasse</Label>
                    {!isEm && sellerRow ? (
                      <Select value={f.share_class} onValueChange={(v) => setF({ ...f, share_class: v })}>
                        <SelectTrigger className="mt-1.5" data-testid="wizard-class"><SelectValue placeholder="Klasse" /></SelectTrigger>
                        <SelectContent>{sellerClasses.map((k) => <SelectItem key={k} value={k}>{k} ({nb(sellerRow.holdings[k])})</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Input className="mt-1.5" data-testid="wizard-class" value={f.share_class} onChange={(e) => setF({ ...f, share_class: e.target.value })} placeholder="Ordinære aksjer" />
                    )}
                  </div>
                  <div><Label className="text-xs">Antall aksjer</Label><Input type="number" className="mt-1.5" data-testid="wizard-shares" value={f.shares} onChange={(e) => setF({ ...f, shares: e.target.value })} /></div>
                  <div><Label className="text-xs">Kurs (kr)</Label><Input type="number" className="mt-1.5" data-testid="wizard-price" value={f.price_per_share} onChange={(e) => setF({ ...f, price_per_share: e.target.value })} /></div>
                </div>

                <div>
                  <Label className="text-xs">{cpLabel}</Label>
                  <div className="mt-1.5 flex gap-1.5 rounded-lg border border-border p-1">
                    {[["existing", "Eksisterende"], ["new_person", "Ny person"], ["new_company", "Ny bedrift"]].map(([v, l]) => (
                      <button key={v} type="button" data-testid={`wizard-cp-${v}`} onClick={() => setF({ ...f, cpKind: v })}
                        className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${f.cpKind === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>{l}</button>
                    ))}
                  </div>
                  {f.cpKind === "existing" ? (
                    <Select value={f.cp_shareholder_id} onValueChange={(v) => setF({ ...f, cp_shareholder_id: v })}>
                      <SelectTrigger className="mt-2" data-testid="wizard-cp-existing"><SelectValue placeholder={`Velg ${cpLabel.toLowerCase()}`} /></SelectTrigger>
                      <SelectContent>{rows.filter((r) => r.id !== f.seller_shareholder_id).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <div className="mt-2 grid grid-cols-2 gap-3">
                      <div className="col-span-2"><Label className="flex items-center gap-1.5 text-xs">Navn
                        {f.cpKind === "new_company" && f.cp_name_verified ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600" data-testid="wizard-cp-name-verified">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Bekreftet mot Proff
                          </span>
                        ) : null}
                      </Label><Input className="mt-1.5" data-testid="wizard-cp-name" value={f.cp_name} onChange={(e) => setF({ ...f, cp_name: e.target.value, cp_name_verified: false })} placeholder={f.cpKind === "new_company" ? "Selskapsnavn AS" : "Fornavn Etternavn"} /></div>
                      <div><Label className="text-xs">{f.cpKind === "new_company" ? "Org.nr" : "Fødselsnummer (valgfritt)"}</Label><Input className="mt-1.5" data-testid="wizard-cp-idnr" value={f.cp_id_number} onChange={(e) => setF({ ...f, cp_id_number: e.target.value, cp_name_verified: false })} placeholder={f.cpKind === "new_person" ? "Kommer via BankID" : ""} /></div>
                      {f.cpKind === "new_person" ? <div><Label className="text-xs">E-post</Label><Input className="mt-1.5" type="email" data-testid="wizard-cp-email" value={f.cp_email} onChange={(e) => setF({ ...f, cp_email: e.target.value })} placeholder="navn@epost.no" /></div> : null}
                    </div>
                  )}
                  {cpIsCompany ? (
                    <div className="mt-2">
                      <SignatoryFields companyName={f.cpKind === "existing" ? cpRow?.name : f.cp_name} testIdPrefix="wizard-cp-signatory"
                        orgNumber={f.cpKind === "existing" ? cpRow?.id_number : f.cp_id_number}
                        onProffName={f.cpKind === "existing" ? undefined : (n) => setF((prev) => ({ ...prev, cp_name: n }))}
                        onVerified={f.cpKind === "existing" ? undefined : () => setF((prev) => ({ ...prev, cp_name_verified: true }))}
                        value={f.cp_signatories} onChange={(v) => setF({ ...f, cp_signatories: v })} />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-border/60 pt-3">
              <Button variant="ghost" className="gap-1.5" data-testid="wizard-back-btn" onClick={() => setPhase("interview")}><ArrowLeft className="h-4 w-4" /> Tilbake til spørsmål</Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" className="gap-1.5" data-testid="wizard-download-btn" onClick={downloadDraft} disabled={streaming || downloading || !currentMd}>
                  {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Last ned avtale
                </Button>
                <Button className="gap-2" data-testid="wizard-create-btn" onClick={create} disabled={saving || streaming || !currentMd}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />} {isEm ? "Opprett emisjon" : "Opprett avtale"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
